import flatten from 'lodash-es/flatten';
import groupBy from 'lodash-es/groupBy';
import orderBy from 'lodash-es/orderBy';
import { createSelector } from 'reselect';
import { IState } from '..';
import { IOption, IOptionGroup } from '../../components/Select';
import { getPermissions } from '../auth';
import { LoadType } from './lookupEnums';
import { lookupEntityAdapters } from './lookupsEntityAdapters';
import {
  ICoOpCampaignType,
  ICoOpState,
  INewDealerRequestEventType,
  IProspectLifecycleStep,
  IRegion,
  IStateLookup,
  OnDemandLookupTypes,
  PriceDiscountGroupType
} from './lookupsInterfaces';

export function getSalesCallObjectiveOptions(state: IState, salesCallTypeId: number): IOption[] {
  const objectives: IOption[] = state.lookups.salesCallObjectives.items
    .filter(o => o.salesCallTypeId === salesCallTypeId && !o.removed)
    .map(x => ({ value: x.objectiveId, label: x.objective } as IOption));

  return orderBy(objectives, (x: IOption) => x.label);
}

export function getObjectiveString(state: IState, objectives: number[]): string {
  return objectives.map(id => state.lookups.salesCallObjectives.hash[id]).join(', ');
}

export function getBrandStringFromId(state: IState, brandId: number): string {
  const brand = state.lookups.brands.values.find(x => x.id === brandId);
  return brand ? brand.brand : 'Unknown';
}

export function getBrandStringFromAbbreviation(state: IState, brand: string): string {
  return state.lookups.brands.hash[brand] ? state.lookups.brands.hash[brand] : brand;
}

export function getBrandsString(state: IState, brands: string[]): string {
  return brands.map(id => state.lookups.brands.hash[id]).join(', ');
}

export function getBrandAbbreviations(state: IState): string[] {
  return state.lookups.brands.values.map(x => x.abbreviation);
}

export function getBrandFromAbbreviation(state: IState, abbreviation: string): string {
  return state.lookups.brands.hash[abbreviation] ? state.lookups.brands.hash[abbreviation] : abbreviation;
}

export function getAddressType(state: IState, addressTypeId: number): string {
  return state.lookups.addressTypes.hash[addressTypeId]
    ? state.lookups.addressTypes.hash[addressTypeId]
    : 'Uknown Address Type';
}

export function lookupsLoaded(state: IState, type: OnDemandLookupTypes): boolean {
  return state.lookups.loaded.includes(type);
}

export function getNegotiatingTacticsString({ lookups: { negotiatingTactics } }: IState, tacticIds: number[]): string {
  const { hash } = negotiatingTactics;
  return tacticIds.map(x => (hash[x] ? hash[x] : 'Unknown')).join(', ');
}

export function getProspectLifeCycleStep(state: IState, prospectLifeCycleStepId: number | undefined): string {
  const {
    prospectLifeCycleSteps: { hash }
  } = state.lookups;

  return prospectLifeCycleStepId && hash[prospectLifeCycleStepId] ? hash[prospectLifeCycleStepId].name : 'Unknown';
}

export function getSurroundingLifeCycleSteps(
  {
    lookups: {
      prospectLifeCycleSteps: { hash, ids }
    }
  }: IState,
  prospectLifeCycleStepId: number | undefined
): { previous: string; next: string } {
  let previous = 'Unknown';
  let next = 'Unknown';

  if (prospectLifeCycleStepId) {
    const currentStatus = hash[prospectLifeCycleStepId];
    if (currentStatus) {
      const statuses = orderBy(
        ids.map(id => hash[id]),
        (x: IProspectLifecycleStep) => x.order
      );
      const previousStatus = statuses.filter(s => s.order < currentStatus.order).reverse()[0];
      if (previousStatus) {
        previous = previousStatus.name;
      }

      const nextStatus = statuses.filter(s => s.order > currentStatus.order)[0];
      if (nextStatus) {
        next = nextStatus.name;
      }
    }
  }

  return { previous, next };
}

export function getRegions({ lookups }: IState): IRegion[] {
  return lookups.regions.ids.map(x => lookups.regions.hash[x]);
}

export function getRegionName({ lookups: { regions } }: IState, regionId: number): string {
  const region = regions.hash[regionId];

  if (region) {
    return region.name;
  }
  return '';
}

export function getSalesDistrictWithDescription(state: IState, salesDistrict: string) {
  const salesDist = state.lookups.salesDistricts.hash[salesDistrict];

  return salesDist ? `${salesDist.salesDistrictId} -  ${salesDist.name}` : salesDistrict;
}

export function getSalesDistricts(state: IState, type: 'global' | 'user'): string[] {
  const permissions = getPermissions(state);
  let sdIds = state.lookups.salesDistricts.ids;

  if (type === 'user' && permissions && permissions.sales) {
    sdIds = permissions.sales.salesDistricts;
  }
  return sdIds;
}

export function getSalesDistrictDescription(state: IState, salesDistrict: string): string {
  const value = state.lookups.salesDistricts.hash[salesDistrict];

  return value ? `${salesDistrict} - ${value.name}` : salesDistrict;
}

export function getSalesDistrictOptions(state: IState, type: 'global' | 'user' = 'global'): IOption[] {
  const permissions = getPermissions(state);
  let sdIds = state.lookups.salesDistricts.ids;
  const { hash } = state.lookups.salesDistricts;

  if (type === 'user' && permissions && permissions.sales) {
    sdIds = permissions.sales.salesDistricts;
  }

  return sdIds
    .map(x => hash[x])
    .filter(x => x)
    .map<IOption>(x => ({
      label: x.salesDistrictId,
      secondaryLabel: x.name,
      value: x.salesDistrictId
    }));
}

export function getBrandOptions(state: IState, type: 'abbreviation' | 'id'): IOption[] {
  const { values } = state.lookups.brands;

  return values.map<IOption>(x => ({
    label: x.brand,
    secondaryLabel: x.abbreviation,
    value: x[type]
  }));
}

export function getPaymentTermOptions(state: IState): IOption[] {
  return state.lookups.paymentTerms.map<IOption>(x => ({
    label: x,
    value: x
  }));
}

export function getPriceDiscountOptions(state: IState, type: PriceDiscountGroupType): IOption[] {
  return state.lookups.priceDiscountGroups
    .filter(x => x.typeId == type)
    .map(x => x)
    .map<IOption>(x => ({
      label: x.groupId,
      secondaryLabel: x.name,
      value: x.groupId
    }));
}

export function getDeliveryModeOptoins(state: IState): IOption[] {
  return state.lookups.deliveryModes.items.map<IOption>(x => ({
    label: x.deliveryModeId,
    secondaryLabel: x.description,
    value: x.deliveryModeId
  }));
}

export function getRegionOptions(state: IState, type: 'global' | 'user' = 'global'): IOption[] {
  const permissions = getPermissions(state);
  let regionIds = state.lookups.regions.ids;
  const { hash } = state.lookups.regions;

  if (type === 'user' && permissions && permissions.sales) {
    regionIds = permissions.sales.regions;
  }

  return regionIds
    .map(x => hash[x])
    .filter(x => x)
    .map<IOption>(x => ({
      label: x.name,
      secondaryLabel: x.name,
      value: x.regionId
    }));
}

export function getStateOptions(state: IState, type: 'id' | 'abbreviation' = 'id'): IOptionGroup[] {
  const stateIds = state.lookups.states.ids;
  const { hash } = state.lookups.states;

  const stateLookups: IStateLookup[] = stateIds.map(x => hash[x]).filter(x => x);

  const grouped = groupBy(stateLookups, (s: IStateLookup) => s.country);

  const optGroup: IOptionGroup[] = Object.entries(grouped).map<IOptionGroup>(([country, states]) => ({
    groupLabel: country,
    options: states.map<IOption>(x => ({
      label: x.name,
      value: x[type]
    })),
    sortLabel: country === 'Canada' ? '2' : '1'
  }));
  return optGroup;
}

export function getRegionSalesDistricts(state: IState, regionIds: number[]): string[] {
  return flatten(
    regionIds
      .map(x => state.lookups.regions.hash[x])
      .filter(x => x)
      .map(x => x.salesDistricts)
  );
}

export function getCoOpCampaignTypes(state: IState, includeRemoved?: boolean): ICoOpCampaignType[] {
  const { ids, hash } = state.lookups.coOpCampaignTypes;

  return ids.map(x => hash[x]).filter(x => (includeRemoved ? true : !x.removed));
}

export function getCoOpStateName({ lookups: { coOpStates } }: IState, coOpStateId: number): string {
  return coOpStates.hash[coOpStateId] ? coOpStates.hash[coOpStateId].name : 'Unknown';
}

export function getCoOpStates({ lookups: { coOpStates } }: IState): ICoOpState[] {
  const states = coOpStates.ids.map(x => coOpStates.hash[x]);
  return orderBy(states, (s: ICoOpState) => s.order);
}

export function getCoOpStateOptions(state: IState): IOption[] {
  return getCoOpStates(state).map<IOption>(x => ({
    label: x.name,
    secondaryLabel: x.description,
    sortLabel: x.order.toString(),
    value: x.coOpStateId
  }));
}

export function getCoOpCampaignType(state: IState, coOpCampaignTypeId: number): string {
  return state.lookups.coOpCampaignTypes.hash[coOpCampaignTypeId]
    ? state.lookups.coOpCampaignTypes.hash[coOpCampaignTypeId].name
    : 'Unknown';
}

export function getOrderedCoOpStates(state: IState): ICoOpState[] {
  const { hash, ids } = state.lookups.coOpStates;

  return orderBy(
    ids.map(x => hash[x]),
    (x: ICoOpState) => x.order
  );
}

export function getDisplayTypesAsOptions({ lookups: { displayTypes } }: IState): IOption[] {
  return displayTypes.ids
    .map(d => displayTypes.hash[d])
    .filter(d => d)
    .map<IOption>(d => ({
      label: d.name,
      value: d.displayTypeId
    }));
}

export const getIsLookupLoading = createSelector(
  [(state: IState) => state.lookups.loading, (_: IState, type: OnDemandLookupTypes | OnDemandLookupTypes[]) => type],
  (loadingArr, type) => (Array.isArray(type) ? type.every(x => loadingArr.includes(x)) : loadingArr.includes(type))
);

export const getIsLookupLoaded = createSelector(
  [(state: IState) => state.lookups.loaded, (_: IState, type: OnDemandLookupTypes | OnDemandLookupTypes[]) => type],
  (loadedArr, type) => (Array.isArray(type) ? type.every(x => loadedArr.includes(x)) : loadedArr.includes(type))
);

export function getDisplayTypeName(state: IState, displayTypeId: number): string {
  const {
    lookups: {
      displayTypes: { hash }
    }
  } = state;

  return hash[displayTypeId] ? hash[displayTypeId].name : 'Unknown';
}

export function getStateAbbreviation(state: IState, stateId: number = -1): string {
  return state.lookups.states.hash[stateId] ? state.lookups.states.hash[stateId].abbreviation : 'Unknown';
}

export function getBigCommercePricingTypeOptions(state: IState): IOption[] {
  return state.lookups.bigCommercePricingTypes.items.map<IOption>(x => ({
    label: x.pricingType,
    value: x.bigCommercePricingTypeId
  }));
}

export function getPurposeCodeOptions(state: IState): IOption[] {
  return state.lookups.purposeCodes.items.map<IOption>(x => ({
    label: x.purposeCodeId,
    secondaryLabel: x.description,
    value: x.purposeCodeId
  }));
}

export function getSalesOriginOptions(state: IState): IOption[] {
  return state.lookups.salesOrigins.items.map<IOption>(x => ({
    label: x.description,
    secondaryLabel: x.originId,
    value: x.originId
  }));
}

export const getWarehouseGroups = createSelector(
  [(state: IState) => state.lookups.warehouseGroups.items, (_: unknown, departmentId: number) => departmentId],
  (groups, departmentId) =>
    orderBy(
      groups.filter(x => x.departmentId === departmentId),
      x => x.name
    )
);

export function getWarehouseOptions(state: IState): IOption[] {
  return state.lookups.warehouses.items.map<IOption>(x => ({
    label: x.warehouseId,
    secondaryLabel: x.name,
    value: x.warehouseId
  }));
}

export function getDeliveryModeOptions(state: IState): IOption[] {
  return state.lookups.deliveryModes.items.map<IOption>(x => ({
    label: x.deliveryModeId,
    secondaryLabel: x.description,
    value: x.deliveryModeId
  }));
}

export const getWarehouse = (warehouseId: string) =>
  createSelector(
    (state: IState) => state.lookups.warehouses.items,
    warehouses => warehouses.find(x => x.warehouseId === warehouseId)
  );

export const getWarehouseColor = (warehouseId: string) =>
  createSelector(getWarehouse(warehouseId), warehouse => (warehouse?.color ? warehouse.color : 'grey'));

export function getNewDealerRequestEventTypes(
  state: IState,
  includeRemoved: boolean = false
): INewDealerRequestEventType[] {
  return orderBy(
    state.lookups.newDealerRequestEventTypes.items.filter(x => !x.removed || includeRemoved),
    (e: INewDealerRequestEventType) => e.order
  );
}

export const getNonLoadedLookups = createSelector(
  [(state: IState) => state.lookups.loaded, (_: unknown, types: OnDemandLookupTypes[]) => types],
  (loaded, types) => types.filter(x => !loaded.includes(x))
);

export const getNonLoadedOrLoadingLookups = createSelector(
  [
    (state: IState) => [...state.lookups.loaded, ...state.lookups.loading],
    (_: unknown, types: OnDemandLookupTypes[]) => types
  ],
  (loaded, types) => types.filter(x => !loaded.includes(x))
);

export const getShipmentContainerType = (containerTypeId: number) =>
  createSelector(
    (state: IState) => state.lookups.shipmentContainerTypes.items,
    items => items.find(x => x.shipmentContainerTypeId === containerTypeId)
  );

export const getShipmentContainerTypes = createSelector(
  (state: IState) => state.lookups.shipmentContainerTypes.items,
  x => x
);

export const getLoadTypeIdFromShipMethod = createSelector(
  [(state: IState) => state.lookups.loadTypes.items, (_: IState, shipMethod: string) => shipMethod],
  (loadTypes, shipMethod): LoadType => {
    const loadType = loadTypes.find(x => x.axDeliveryMode === shipMethod);

    if (loadType) {
      return loadType.loadTypeId;
    } else if (shipMethod === 'CUSTTRUCK') {
      return LoadType.CustomerPickup;
    } else {
      return LoadType.Truck;
    }
  }
);

export const companyDimensionSelectors = lookupEntityAdapters.companyDimensions.getSelectors<IState>(
  state => state.lookups.companyDimensions
);

export const pickListSelectors = lookupEntityAdapters.pickLists.getSelectors<IState>(state => state.lookups.pickLists);
export const dpShipMethodSelectors = lookupEntityAdapters.dpShipMethods.getSelectors<IState>(
  state => state.lookups.dpShipMethods
);
